import { addAlertThunk, clearAlertsThunk } from 'actions/alerts/AlertsActions';
import {
    authenticateCustomerBySMSCodeThunk,
    requestAuthenticationCodeThunk
} from 'actions/customer/CustomerActions';
import clsx from 'clsx';
import ErrorList from 'components/Form/ErrorList/ErrorList';
import { Input } from 'components/Form/Input/Input';
import { PhoneInput } from 'components/Form/PhoneInput/PhoneInput';
import { Button } from 'components/UI/Button/Button';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import { getCustomerState } from 'reducers/customer/CustomerSelectors';
import { CustomerStatus } from 'reducers/customer/CustomerState';
import { dispatch } from 'store';
import { AlertScreen } from 'types/Alert';
import { authenticateOnlineBookingCustomerThunk } from 'actions/onlineBooking/actions/OnlineBookingCustomerActions';
import classes from './OnlineBookingSigninForm.module.scss';

// Two minutes
const TIMEOUT = 2 * 60 * 1000;

const getTimerRemainingMinutes = (timerStartTime: number | null) => {
    if (!timerStartTime) {
        return '';
    }

    const remainingTimeMs = TIMEOUT - (new Date().getTime() - timerStartTime);
    const remainingTimeMinutes = Math.round(remainingTimeMs / 60000);

    return `${remainingTimeMinutes === 0 ? 1 : remainingTimeMinutes} ${
        remainingTimeMinutes > 1 ? 'minutes' : 'minute'
    }`;
};

export const OnlineBookingSigninForm: React.FC = () => {
    const marketplace = useMarketplace();

    const [code, setCode] = React.useState<string>('');
    const [phone, setPhone] = React.useState<string>('');
    const [touched, setTouched] = React.useState<boolean>(false);
    const [isRequested, setIsRequested] = React.useState<boolean>(false);
    const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);
    const [timerStartTime, setTimerStartTime] = React.useState<number | null>(null);

    const { customer, loading: isLoading, status: customerStatus } = useSelector(getCustomerState);

    const phoneWithoutSpecialChars = React.useMemo(() => {
        return phone?.replace(/[()\-_ ]/g, '');
    }, [phone]);

    const isPhoneValid = React.useMemo(() => {
        if (!phone) return false;

        return phoneWithoutSpecialChars?.length === 10;
    }, [phone, phoneWithoutSpecialChars]);

    const isCodeValid = React.useMemo(() => {
        return code?.length === 6;
    }, [code]);

    const resetTimer = () => {
        setTimer(null);
        setTimerStartTime(null);
    };

    const startTimer = () => {
        setTimerStartTime(new Date().getTime());

        setTimer(
            setTimeout(() => {
                resetTimer();
            }, TIMEOUT)
        );
    };

    const submitHandler = (resend = false) => {
        if (!isPhoneValid) {
            return;
        }

        dispatch(clearAlertsThunk());

        // If timer it's set skip sending the code & its resending process
        if (timer !== null && resend) {
            const remainingTime = getTimerRemainingMinutes(timerStartTime);

            dispatch(
                addAlertThunk(
                    `You already requested a code, please wait ${remainingTime} before sending a new one.`,
                    AlertScreen.OnlineBookingSignin
                )
            );

            return;
        }

        startTimer();

        dispatch(
            requestAuthenticationCodeThunk(
                phoneWithoutSpecialChars,
                AlertScreen.OnlineBookingSignin
            )
        );
    };

    const confirmCodeHandler = async () => {
        if (!isCodeValid) {
            return;
        }

        dispatch(clearAlertsThunk());
        const { token, customer } = await dispatch(
            authenticateCustomerBySMSCodeThunk(
                phoneWithoutSpecialChars,
                code,
                AlertScreen.OnlineBookingSignin
            )
        );

        dispatch(authenticateOnlineBookingCustomerThunk(token, customer));
    };

    React.useEffect(() => {
        // If the user already requested a code, we should show the code form
        if (customerStatus === CustomerStatus.RequestAuthCodeSuccess) {
            setIsRequested(true);
        }
        // eslint-disable-next-line
    }, [customerStatus, customer]);

    if (isRequested) {
        return (
            <>
                <h2 className={clsx('h3 underlined', classes.title, classes.subtitle)}>
                    We sent you a code to {phone}
                </h2>

                <div className={classes.form}>
                    <Input
                        required
                        value={code}
                        label="Enter the code"
                        inputProps={{ maxLength: 6 }}
                        onChange={event => setCode(event.target.value)}
                    />

                    <ErrorList screen={AlertScreen.OnlineBookingSignin} />

                    <div className={classes.footer}>
                        <div className="container px-0">
                            <Button
                                fullWidth
                                disabled={!isCodeValid || isLoading}
                                onClick={confirmCodeHandler}
                            >
                                Sign in
                            </Button>
                        </div>
                    </div>

                    <div className="text-center">
                        <Button variant="underlined-link" onClick={() => submitHandler(true)}>
                            Resend code
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <h1 className={clsx('h1 underlined', classes.title)}>Sign in to {marketplace.name}</h1>

            <div className={classes.form}>
                <PhoneInput
                    required
                    label="Phone Number"
                    value={phone}
                    error={!isPhoneValid && touched}
                    onBlur={() => setTouched(true)}
                    onChange={setPhone}
                />

                <ErrorList screen={AlertScreen.OnlineBookingSignin} />

                <div className={classes.footer}>
                    <div className="container px-0">
                        <Button
                            fullWidth
                            disabled={!isPhoneValid || isLoading}
                            onClick={() => submitHandler()}
                        >
                            Request Code
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};
